import React, { useState, useEffect } from "react";
import api from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";

function ProductManagement() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [type, setType] = useState("product");
  const [formData, setFormData] = useState({
    name: "",
    categoryId: "",
    isActive: 1,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catData, prodData] = await Promise.all([
        api.getMasterData("categories"),
        api.getMasterData("products"),
      ]);
      setCategories(catData || []);
      setProducts(prodData || []);
    } catch (err) {
      console.error("Failed to load data:", err);
      alert("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let data = { name: formData.name, isActive: formData.isActive };

      if (type === "category") {
        await api.createMasterData("categories", data);
      } else {
        await api.createMasterData("products", {
          ...data,
          categoryId: formData.categoryId || null,
        });
      }
      loadData();
      setShowAdd(false);
      setFormData({ name: "", categoryId: "", isActive: 1 });
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save");
    }
  };

  const handleDelete = async (id, type) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      try {
        let endpoint = type === "category" ? "categories" : "products";
        await api.deleteMasterData(endpoint, id);
        loadData();
      } catch (err) {
        console.error("Failed to delete:", err);
        alert("Failed to delete");
      }
    }
  };

  if (loading) return <LoadingSpinner message="Loading products..." />;

  return (
    <div className="product-management">
      <div className="management-header">
        <h2>Product Management</h2>

        <div className="header-actions">
          <div className="admin-tabs" style={{ margin: 0 }}>
            <button
              type="button"
              className={`admin-tab ${type === "category" ? "active" : ""}`}
              onClick={() => setType("category")}
            >
              Categories
            </button>

            <button
              type="button"
              className={`admin-tab ${type === "product" ? "active" : ""}`}
              onClick={() => setType("product")}
            >
              Products
            </button>
          </div>

          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <i className="fas fa-plus"></i> Add {type}
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add {type}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              {type === "product" && (
                <div className="form-group">
                  <label>Category</label>
                  <select
                    className="form-control"
                    value={formData.categoryId}
                    onChange={(e) =>
                      setFormData({ ...formData, categoryId: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Active</label>
                <select
                  className="form-control"
                  value={formData.isActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isActive: parseInt(e.target.value),
                    })
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAdd(false);
                    setFormData({
                      name: "",
                      categoryId: "",
                      isActive: 1,
                    });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {type === "category" &&
              categories.map((cat) => (
                <tr key={cat.id}>
                  <td>
                    <strong>{cat.name}</strong>
                  </td>
                  <td>
                    <span className="badge badge-info">Category</span>
                  </td>
                  <td>-</td>
                  <td>
                    <span
                      className={`status-badge ${cat.is_active ? "active" : "inactive"}`}
                    >
                      {cat.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(cat.id, "category")}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}

            {type === "product" &&
              products.map((prod) => (
                <tr key={prod.id}>
                  <td>
                    <strong>{prod.name}</strong>
                  </td>
                  <td>
                    <span className="badge badge-success">Product</span>
                  </td>
                  <td>
                    {categories.find((c) => c.id === prod.category_id)?.name ||
                      "—"}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${prod.is_active ? "active" : "inactive"}`}
                    >
                      {prod.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(prod.id, "product")}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ProductManagement;
